import { Card, CenteredSpinner, Col, Row, Title } from '@wyz/ui';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useManageCriteria } from '~/common/api';
import { SelectField } from '~/common/components';
import { Trans, useI18n } from '~/common/context';
import { RESERVED_CRITERIA_CODE } from '~/common/schemas';
import { CriteriaHelpers } from '~/connect/helpers';
import { ICriteria, ICustomerCriteriaForm } from '~/connect/types';

const getSelectedValue = (criterion: ICriteria, valueId?: string) =>
  criterion.values.find((value) => value.id === valueId);

/**
 * A selected value hidden for the enseigne is kept (and shown with a warning) unless it's the default one
 */
const isStale = (criterion: ICriteria, valueId?: string) => {
  const selectedValue = getSelectedValue(criterion, valueId);
  return selectedValue !== undefined && !selectedValue.available;
};

const isDisplayed = (criterion: ICriteria, valueId?: string) =>
  criterion.available !== false ||
  (isStale(criterion, valueId) &&
    !getSelectedValue(criterion, valueId)?.default);

const CriteriaForm = () => {
  const { t } = useI18n();
  const methods = useFormContext<Pick<ICustomerCriteriaForm, 'criteria'>>();
  const formCriteria = useWatch({ control: methods.control, name: 'criteria' });
  const [automakerGroupCriterionId, setAutomakerGroupCriterionId] =
    React.useState<string>();
  const selectedAutomakerGroupId = formCriteria?.find(
    (item) => item?.criterion?.id === automakerGroupCriterionId,
  )?.value;

  // the values are flagged as available for the selected enseigne, the list itself (and its order) never changes
  const manageCriteria = useManageCriteria({
    getAll: {
      enabled: true,
      args: {
        includeInternal: false,
        restrictedToFilter: true,
        ...(selectedAutomakerGroupId
          ? { automakerGroupIds: [selectedAutomakerGroupId] }
          : {}),
      },
      placeholderData: (previousData) => previousData,
    },
  });
  const criteria = manageCriteria.getAll.data?.data;

  React.useEffect(() => {
    setAutomakerGroupCriterionId(
      criteria?.find(
        (criterion) =>
          criterion.code === RESERVED_CRITERIA_CODE.AUTOMAKER_GROUP,
      )?.id,
    );

    criteria?.forEach((criterion, index) => {
      const formCriterion = methods.getValues(`criteria.${index}`);
      if (formCriterion?.criterion?.id !== criterion.id) {
        methods.setValue(`criteria.${index}.criterion.id`, criterion.id);
      }
      const defaultValue = criterion.values.find((value) => value.default);
      if (
        !isDisplayed(criterion, formCriterion?.value) &&
        !formCriterion?.value &&
        defaultValue
      ) {
        methods.setValue(`criteria.${index}.value`, defaultValue.id);
      }
    });
  }, [criteria, methods]);

  return (
    <Card>
      <Card.Header>
        <Title heading='lg' color='tertiary' weight='bold'>
          <Trans i18nKey='app.common.criteria' />
        </Title>
      </Card.Header>
      <Card.Body>
        <Row>
          {!criteria ? (
            <CenteredSpinner />
          ) : (
            criteria.map((criterion, index) => {
              const valueId = formCriteria?.[index]?.value;
              if (!isDisplayed(criterion, valueId)) {
                return null;
              }
              const stale = isStale(criterion, valueId);

              return (
                <Col md={4} key={criterion.id} className='position-relative'>
                  <input
                    type='hidden'
                    {...methods.register(`criteria.${index}.criterion.id`)}
                    value={criterion.id}
                  />
                  <SelectField<true>
                    label={criterion.translatedName ?? criterion.name}
                    name={`criteria.${index}.value`}
                    options={CriteriaHelpers.sorterByName(
                      criterion.values.filter(
                        (value) => value.available || value.id === valueId,
                      ),
                    ).map((criterionValue) => ({
                      label: criterionValue.label,
                      value: criterionValue.id,
                    }))}
                    isOptionDisabled={(option) =>
                      stale && option.value === valueId
                    }
                    warningText={
                      stale
                        ? t('app.criteria.value.not_available_warning')
                        : undefined
                    }
                    required
                  />
                </Col>
              );
            })
          )}
        </Row>
      </Card.Body>
    </Card>
  );
};

export default CriteriaForm;
